import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "context/AuthContext";
import { apiDelete, apiGet, apiPost, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import ConfirmationModal from "components/reusable/ConfirmationModal";

const emptyForm = {
  name: "",
  network: "",
  countryName: "",
  countryCode: "",
  stateName: "",
  stateCode: "",
  city: "",
  notes: "",
};

const GeoAreaLocations = () => {
  const { t } = useTranslation();
  const { updatePageName } = useContext(AuthContext);
  const notification = useToastContext();
  const [policy, setPolicy] = useState(null);
  const [offices, setOffices] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [ipValue, setIpValue] = useState("");
  const [ipLabel, setIpLabel] = useState("");
  const [ipListType, setIpListType] = useState("allow");
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const load = async () => {
    const [policyRes, officeRes, masterRes] = await Promise.all([
      apiGet(apiPath.hrGeoPolicy),
      apiGet(apiPath.hrGeoOffices, { page: 1, pageSize: 50 }),
      apiGet(apiPath.getCountryCityList),
    ]);
    if (policyRes?.data?.success) setPolicy(policyRes.data.results);
    if (officeRes?.data?.success) setOffices(officeRes.data.results?.docs || []);
    setCountries(masterRes?.data?.results?.country || []);
    setStates(masterRes?.data?.results?.state || []);
  };

  useEffect(() => {
    updatePageName(t("GEO_AREA_LOCATIONS"));
    load();
  }, []);

  const savePolicyFlag = async (partial) => {
    const res = await apiPut(apiPath.hrGeoPolicy, { ...policy, ...partial });
    if (res?.data?.success) {
      setPolicy({
        ...res.data.results,
        currentIp: res.data.results?.currentIp || policy?.currentIp,
      });
      notification.success(res.data.message);
      return true;
    }
    notification.error(res?.data?.message);
    return false;
  };

  const addCompanyIpValue = async (value, type, label = "") => {
    if (!value) return;
    const key = type === "block" ? "ipBlocklist" : "ipAllowlist";
    const otherKey = type === "block" ? "ipAllowlist" : "ipBlocklist";
    const list = policy[key] || [];
    if (list.some((i) => i.value === value)) {
      notification.error("This IP is already in the list");
      return;
    }
    await savePolicyFlag({
      [key]: [...list, { value, label }],
      [otherKey]: (policy[otherKey] || []).filter((i) => i.value !== value),
    });
  };

  const addCompanyIp = () => {
    if (!ipValue.trim()) return;
    addCompanyIpValue(ipValue.trim(), ipListType, ipLabel.trim());
    setIpValue("");
    setIpLabel("");
  };

  const askRemove = (name, run, fromList) => setRemoveConfirm({ name, run, fromList });

  const removeCompanyIp = (key, value) => {
    savePolicyFlag({
      [key]: (policy[key] || []).filter((i) => i.value !== value),
    });
  };

  const allowBlockedCompanyIp = (entry) => {
    savePolicyFlag({
      ipBlocklist: (policy.ipBlocklist || []).filter((i) => i.value !== entry.value),
      ipAllowlist: (policy.ipAllowlist || []).some((i) => i.value === entry.value)
        ? policy.ipAllowlist
        : [...(policy.ipAllowlist || []), { value: entry.value, label: entry.label || "" }],
    });
  };

  const submitOffice = async (e) => {
    e.preventDefault();
    const api = editingId
      ? (body) => apiPut(`${apiPath.hrGeoOffices}/${editingId}`, body)
      : (body) => apiPost(apiPath.hrGeoOffices, body);
    const res = await api(form);
    if (res?.data?.success) {
      notification.success(res.data.message);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } else {
      notification.error(res?.data?.message);
    }
  };

  const removeOffice = async (id) => {
    const res = await apiDelete(`${apiPath.hrGeoOffices}/${id}`);
    if (res?.data?.success) {
      notification.success(res.data.message);
      load();
    } else {
      notification.error(res?.data?.message);
    }
  };

  const countryStates = states.filter(
    (s) => String(s.countryID) === String(form.countryId || ""),
  );

  return (
    <div className="bg-[#F9F9F9]">
      <div className="px-3 py-4">
        <div className="bg-white border border-[#E9EDF9] rounded-lg p-4">
          <div className="flex flex-wrap justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold">{t("GEO_AREA_LOCATIONS")}</h2>
              <p className="text-sm text-gray-500">{t("GEO_AREA_HELP")}</p>
            </div>
            {policy && (
              <div className="flex flex-col gap-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!policy.enabled}
                    onChange={(e) => savePolicyFlag({ enabled: e.target.checked })}
                  />
                  {t("GEO_ENABLE_COMPANY")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!policy.officeAllowlistOnly}
                    onChange={(e) =>
                      savePolicyFlag({ officeAllowlistOnly: e.target.checked })
                    }
                  />
                  {t("GEO_OFFICE_ONLY")}
                </label>
              </div>
            )}
          </div>

          {policy && (
            <div className="mb-8 p-4 rounded-lg border border-[#E9EDF9] bg-[#F8FAFF]">
              <h3 className="font-medium mb-1">Company IP allow / block</h3>
              <p className="text-sm text-gray-500 mb-3">
                HR can allow or block IPs for this company. Turn on company geo
                rules first. Current IP:{" "}
                <strong>{policy.currentIp || "unknown"}</strong>
              </p>
              <label className="flex items-start gap-2 text-sm mb-4">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!policy.ipAllowlistOnly}
                  onChange={(e) =>
                    savePolicyFlag({ ipAllowlistOnly: e.target.checked })
                  }
                />
                <span>
                  Only listed IPs can log in for this company (company admin, HR,
                  accountant, employee). Add your IP before turning this on.
                </span>
              </label>
              <div className="flex flex-wrap gap-2 mb-4">
                <select
                  className="border rounded-lg px-3 py-2"
                  value={ipListType}
                  onChange={(e) => setIpListType(e.target.value)}
                >
                  <option key="allow" value="allow">Allow</option>
                  <option key="block" value="block">Block</option>
                </select>
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder="IP or CIDR"
                  value={ipValue}
                  onChange={(e) => setIpValue(e.target.value)}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder="Label"
                  value={ipLabel}
                  onChange={(e) => setIpLabel(e.target.value)}
                />
                <button
                  type="button"
                  className="bg-LightBlue text-white px-4 py-2 rounded-lg"
                  onClick={addCompanyIp}
                >
                  Add IP
                </button>
                <button
                  type="button"
                  className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
                  onClick={() => {
                    if (!ipValue.trim()) return;
                    addCompanyIpValue(ipValue.trim(), "block", ipLabel.trim());
                    setIpValue("");
                    setIpLabel("");
                  }}
                >
                  Block IP
                </button>
                {policy.currentIp && (
                  <>
                    <button
                      type="button"
                      className="border px-4 py-2 rounded-lg"
                      onClick={() => addCompanyIpValue(policy.currentIp, "allow", "HR computer")}
                    >
                      Allow my IP
                    </button>
                    <button
                      type="button"
                      className="border px-4 py-2 rounded-lg"
                      onClick={() => addCompanyIpValue(policy.currentIp, "block", "HR computer")}
                    >
                      Block my IP
                    </button>
                  </>
                )}
              </div>
              <IpRows
                title="Allowed IPs"
                rows={policy.ipAllowlist}
                onRemove={(value) =>
                  askRemove(value, () => removeCompanyIp("ipAllowlist", value), "Allowed")
                }
              />
              <IpRows
                title="Blocked IPs"
                rows={policy.ipBlocklist}
                onRemove={(value) =>
                  askRemove(value, () => removeCompanyIp("ipBlocklist", value), "Blocked")
                }
                onAllow={allowBlockedCompanyIp}
              />
            </div>
          )}

          <form onSubmit={submitOffice} className="grid md:grid-cols-3 gap-3 mb-6">
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Office name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="IP or CIDR e.g. 203.0.113.0/24"
              value={form.network}
              onChange={(e) => setForm({ ...form, network: e.target.value })}
              required
            />
            <select
              className="border rounded-lg px-3 py-2"
              value={form.countryId || ""}
              onChange={(e) => {
                const country = countries.find((c) => c._id === e.target.value);
                setForm({
                  ...form,
                  countryId: e.target.value,
                  countryName: country?.name || "",
                  countryCode: country?.countryCode || "",
                  stateId: "",
                  stateName: "",
                  stateCode: "",
                });
              }}
            >
              <option value="">Country</option>
              {countries.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="border rounded-lg px-3 py-2"
              value={form.stateId || ""}
              onChange={(e) => {
                const state = countryStates.find((s) => s._id === e.target.value);
                setForm({
                  ...form,
                  stateId: e.target.value,
                  stateName: state?.name || "",
                  stateCode: state?.code || state?.abbreviation || "",
                });
              }}
            >
              <option value="">State</option>
              {countryStates.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="City"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            <div className="md:col-span-3">
              <button
                type="submit"
                className="bg-LightBlue text-white px-5 py-2 rounded-lg"
              >
                {editingId ? "Update location" : "Add location"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="ml-2 border px-4 py-2 rounded-lg"
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm);
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2">Name</th>
                  <th>Network</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {offices.map((office) => (
                  <tr key={office._id} className="border-b">
                    <td className="py-2">{office.name}</td>
                    <td>{office.network}</td>
                    <td>
                      {[office.city, office.stateName, office.countryName]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </td>
                    <td>{office.status}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="text-blue-600 mr-3"
                        onClick={() => {
                          setEditingId(office._id);
                          setForm({
                            name: office.name,
                            network: office.network,
                            countryName: office.countryName,
                            countryCode: office.countryCode,
                            stateName: office.stateName,
                            stateCode: office.stateCode,
                            city: office.city,
                            notes: office.notes || "",
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-500"
                        onClick={() =>
                          askRemove(office.name || office.network, () => removeOffice(office._id))
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {offices.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-gray-400">
                      No office / network locations yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={!!removeConfirm}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={() => {
          removeConfirm?.run?.();
          setRemoveConfirm(null);
        }}
        isDanger
        title="Remove?"
        message={`Do you still want to remove ${removeConfirm?.name || "this"}${
          removeConfirm?.fromList ? ` from ${removeConfirm.fromList}` : ""
        }?`}
        confirmText="Yes, remove"
        cancelText="Cancel"
      />
    </div>
  );
};

export default GeoAreaLocations;

const IpRows = ({ title, rows, onRemove, onAllow }) => (
  <div className="mb-4">
    <h4 className="text-sm font-medium mb-1">{title}</h4>
    <div className="border rounded-lg overflow-hidden bg-white">
      {(rows || []).length === 0 ? (
        <div className="p-3 text-sm text-gray-400">None</div>
      ) : (
        (rows || []).map((row) => (
          <div
            key={row.value}
            className="flex justify-between items-center px-3 py-2 border-b last:border-b-0 text-sm"
          >
            <span>
              {row.value} {row.label ? `— ${row.label}` : ""}
            </span>
            <div className="flex gap-3">
              {onAllow && (
                <button
                  type="button"
                  className="text-blue-600"
                  onClick={() => onAllow(row)}
                >
                  Allow
                </button>
              )}
              <button
                type="button"
                className="text-red-500"
                onClick={() => onRemove(row.value)}
              >
                Remove
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);
